import React, { useState } from 'react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { Calendar, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DateRangeType } from '../../hooks/useYouTubeAnalytics';

interface DateRangePickerProps {
  dateRange: DateRangeType;
  customDateRange: { start: Date; end: Date };
  onDateRangeChange: (newRange: DateRangeType) => void;
  onCustomDateRangeChange: (range: { start: Date; end: Date }) => void;
  baseDate?: Date;
}

export const dateRangeOptions = [
  { key: 'thisMonth', value: 'this-month' },
  { key: 'lastMonth', value: 'last-month' },
  { key: 'last15days', value: 15 },
  { key: 'last30days', value: 30 },
  { key: 'last90days', value: 90 },
  { key: 'last365days', value: 365 },
  { key: 'custom', value: 'custom' }
] as const;

export function DateRangePicker({
  dateRange,
  customDateRange,
  onDateRangeChange,
  onCustomDateRangeChange,
  baseDate = new Date()
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  // Internal state for temporary custom date range
  const [tempCustomStart, setTempCustomStart] = useState<Date>(customDateRange.start);
  const [tempCustomEnd, setTempCustomEnd] = useState<Date>(customDateRange.end);

  // Effect to sync temp dates when the main customDateRange prop changes
  // or when the dropdown opens and current mode is 'custom'.
  React.useEffect(() => {
    if (dateRange === 'custom') {
      setTempCustomStart(customDateRange.start);
      setTempCustomEnd(customDateRange.end);
    }
    // If not 'custom', we might want to reset temp dates to props or keep them.
    // For now, they only explicitly sync when 'custom' is active.
  }, [customDateRange.start, customDateRange.end, dateRange, isOpen]);

  // Compute the displayed date range label
  const displayedRange = (() => {
    if (dateRange === 'custom') {
      return `${format(customDateRange.start, 'MMM d, yyyy')} - ${format(
        customDateRange.end,
        'MMM d, yyyy'
      )}`;
    } else if (dateRange === 'this-month') {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return `${t('analytics.dateRange.thisMonth')} (${format(startOfMonth, 'MMM d, yyyy')} - ${format(now, 'MMM d, yyyy')})`;
    } else if (dateRange === 'last-month') {
      const now = new Date();
      const lastMonthYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
      const startOfLastMonth = new Date(lastMonthYear, lastMonth, 1);
      const endOfLastMonth = new Date(lastMonthYear, lastMonth + 1, 0);
      return `${t('analytics.dateRange.lastMonth')} (${format(startOfLastMonth, 'MMM d, yyyy')} - ${format(
        endOfLastMonth,
        'MMM d, yyyy'
      )})`;
    } else {
      const startDisp = format(subDays(baseDate, Number(dateRange)), 'MMM d');
      const endDisp = format(subDays(baseDate, 1), 'MMM d, yyyy');
      return `${t('analytics.dateRange.last')} ${dateRange} ${t('analytics.dateRange.days')} (${startDisp} - ${endDisp})`;
    }
  })();

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:text-primary-600 focus:outline-none"
      >
        <Calendar className="w-4 h-4 mr-2 text-primary-500" />
        {displayedRange}
        <ChevronDown className="w-4 h-4 ml-2" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-10">
          <div className="p-2">
            {dateRangeOptions.map(option => (
              <button
                key={option.value}
                onClick={() => {
                  if (option.value === 'custom') {
                    // When switching to custom, ensure temp dates are synced with the current prop
                    setTempCustomStart(customDateRange.start);
                    setTempCustomEnd(customDateRange.end);
                    onDateRangeChange('custom');
                  } else if (option.value === 'this-month' || option.value === 'last-month') {
                    onDateRangeChange(option.value);
                  } else {
                    onDateRangeChange(option.value as number);
                    onCustomDateRangeChange({
                      start: startOfDay(subDays(baseDate, Number(option.value))),
                      end: endOfDay(baseDate)
                    });
                  }
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2 text-sm rounded-lg ${
                  dateRange === option.value
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                {t(`analytics.dateRange.${option.key}`)}
              </button>
            ))}
          </div>

          {dateRange === 'custom' && (
            <div className="p-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('analytics.dateRange.startDate')}
                  </label>
                  <input
                    type="date"
                    value={format(tempCustomStart, 'yyyy-MM-dd')}
                    onChange={e => {
                      const date = new Date(e.target.value);
                      // Update internal temp state, not the prop directly
                      setTempCustomStart(startOfDay(date));
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('analytics.dateRange.endDate')}
                  </label>
                  <input
                    type="date"
                    value={format(tempCustomEnd, 'yyyy-MM-dd')}
                    onChange={e => {
                      const date = new Date(e.target.value);
                      // Update internal temp state, not the prop directly
                      setTempCustomEnd(endOfDay(date));
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end space-x-2">
                <button
                  onClick={() => setIsOpen(false)}
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 rounded-lg"
                >
                  {t('analytics.dateRange.cancel')}
                </button>
                <button
                  onClick={() => {
                    // Apply the temporary custom dates
                    onCustomDateRangeChange({ start: tempCustomStart, end: tempCustomEnd });
                    onDateRangeChange('custom'); // Ensure the mode is set to custom
                    setIsOpen(false);
                  }}
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
                >
                  {t('analytics.dateRange.apply')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}